/**
 * Purpose:
 *   Provides path matching helpers for sidebar menu items.
 *
 * Notes:
 *   Centralizes route matching so menu state and tests use the same rules.
 */

import type { MenuItemConfig } from '@/types/menu';

function splitRoute(route: string) {
  const [pathname, search = ''] = route.split('?', 2);
  return { pathname, search };
}

function normalizePath(pathname: string): string {
  if (pathname === '/') return pathname;
  return pathname.replace(/\/+$/, '');
}

function normalizeRoute(route: string): string {
  const { pathname, search } = splitRoute(route);
  const normalizedPathname = normalizePath(pathname);

  return search ? `${normalizedPathname}?${search}` : normalizedPathname;
}

function matchesPathPattern(pattern: string, pathname: string): boolean {
  return new RegExp(pattern).test(pathname);
}

export function isMenuItemActiveForPath(
  item: MenuItemConfig,
  route: string
): boolean {
  const normalizedRoute = normalizeRoute(route);
  const { pathname } = splitRoute(normalizedRoute);
  const normalizedPathname = normalizePath(pathname);

  if (
    item.inactiveRoutePatterns?.some((pattern) =>
      matchesPathPattern(pattern, normalizedRoute)
    )
  ) {
    return false;
  }

  if (item.href && normalizePath(item.href) === normalizedPathname) {
    return true;
  }

  return (
    item.activeRoutePatterns?.some((pattern) =>
      matchesPathPattern(pattern, normalizedRoute)
    ) ?? false
  ) || (
    item.activePathPatterns?.some((pattern) =>
      matchesPathPattern(pattern, normalizedPathname)
    ) ?? false
  );
}

export function findActiveParentItemId(
  items: MenuItemConfig[],
  pathname: string
): string | null {
  const activeParentItem = items.find((item) => {
    if (!item.children) return false;
    return item.children.some((child) => isMenuItemActiveForPath(child, pathname));
  });

  return activeParentItem?.id ?? null;
}
